import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Droplet, Wrench, Eye, Hammer, Lightbulb } from "lucide-react";
import { useLocation } from "wouter";

const services = {
  residential: [
    {
      id: "pumping",
      title: "Septic Pumping",
      icon: Droplet,
      description: "Drains backing up? Water pooling up? Keep your system running smoothly with a scheduled septic tank pump from us.",
      details: "Regular pumping prevents system failures and extends the life of your septic tank.",
    },
    {
      id: "installation",
      title: "Installation",
      icon: Wrench,
      description: "Want a new septic system, or drain field? Put that shovel down... Get your new system completely installed by a friendly local expert.",
      details: "Professional installation ensures your system meets all local codes and regulations.",
    },
    {
      id: "inspection",
      title: "Inspection",
      icon: Eye,
      description: "Need a trained set of eyeballs to tell you the state of your system? For your required inspections, or if you're just curious, we can take a look for you.",
      details: "Comprehensive inspections identify potential issues before they become expensive problems.",
    },
    {
      id: "repairs",
      title: "Repairs",
      icon: Hammer,
      description: "If it's time for a septic fix, we'll make sure you get the right one. Need emergency septic service or something routine? When it's time, give us a call.",
      details: "Emergency repairs available 24/7 to get your system back in working order.",
    },
    {
      id: "design",
      title: "System Design",
      icon: Lightbulb,
      description: "Make sure your septic system meets all the needs of your location and unique usage. Get a system that fits your needs now and later.",
      details: "Expert design ensures optimal performance for your specific property and usage patterns.",
    },
  ],
  commercial: [
    {
      id: "pumping",
      title: "Septic Pumping",
      icon: Droplet,
      description: "Commercial-grade septic pumping services designed for high-volume systems. Keep your business operations running smoothly.",
      details: "Scheduled maintenance programs tailored to your commercial property's needs.",
    },
    {
      id: "installation",
      title: "Installation",
      icon: Wrench,
      description: "Large-scale septic system installation for commercial properties. We handle complex installations with precision.",
      details: "Professional installation with full compliance to commercial building codes.",
    },
    {
      id: "inspection",
      title: "Inspection",
      icon: Eye,
      description: "Comprehensive inspections for commercial septic systems. Ensure compliance and prevent costly downtime.",
      details: "Detailed reports and recommendations for system maintenance and upgrades.",
    },
    {
      id: "repairs",
      title: "Repairs",
      icon: Hammer,
      description: "Rapid response repairs for commercial septic emergencies. Minimize business disruption.",
      details: "Priority service to get your commercial system back online quickly.",
    },
    {
      id: "design",
      title: "System Design",
      icon: Lightbulb,
      description: "Custom septic system design for commercial applications. Handle high-volume waste efficiently.",
      details: "Engineering expertise for complex commercial septic requirements.",
    },
  ],
};

export default function ServicesSection() {
  const [activeTab, setActiveTab] = useState("residential");
  const [, navigate] = useLocation();

  const handleLearnMore = (serviceId: string) => {
    const routes: Record<string, string> = {
      'pumping': '/services/septic-pumping',
      'installation': '/services/septic-installation',
      'inspection': '/services/septic-inspection',
      'repairs': '/services/septic-repairs',
      'design': '/services/septic-system-design',
    };
    
    if (routes[serviceId]) {
      navigate(routes[serviceId]);
    }
  };

  return (
    <section id="services-section" className="py-20 bg-background relative overflow-hidden">
      {/* Background Logo */}
      <img src="/manus-storage/watermark-800_50cb7eab_85566717.webp" srcSet="/manus-storage/watermark-550_5637eab4_c23ac34b.webp 550w, /manus-storage/watermark-800_50cb7eab_85566717.webp 800w, /manus-storage/watermark-1100_194e6bcc_77393d31.webp 1100w" sizes="(min-width: 1280px) 800px, (min-width: 768px) 720px, 100vw" alt="" width={1100} height={1099} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-center opacity-12 z-0" style={{objectFit: 'contain'}} />
      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Our Services
          </h2>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            Whether you need residential or commercial septic services, we have comprehensive solutions for all your needs.
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto mb-12 grid-cols-2">
            <TabsTrigger value="residential" className="text-base">
              Residential
            </TabsTrigger>
            <TabsTrigger value="commercial" className="text-base">
              Commercial
            </TabsTrigger>
          </TabsList>

          {/* Residential Services */}
          <TabsContent value="residential" className="space-y-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.residential.map((service) => {
                const Icon = service.icon;
                return (
                  <Card key={service.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <Icon className="w-8 h-8 text-accent" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-foreground/70">{service.description}</p>
                      <p className="text-sm text-foreground/60">{service.details}</p>
                      <Button
                        variant="outline"
                        className="w-full border-primary text-primary hover:bg-primary/5"
                        onClick={() => handleLearnMore(service.id)}
                      >
                        Learn More »
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center pt-8">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
                onClick={() => {
                  const contactSection = document.getElementById('contact-section');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                See All Our Services »
              </Button>
            </div>
          </TabsContent>

          {/* Commercial Services */}
          <TabsContent value="commercial" className="space-y-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.commercial.map((service) => {
                const Icon = service.icon;
                return (
                  <Card key={service.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <Icon className="w-8 h-8 text-accent" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-foreground/70">{service.description}</p>
                      <p className="text-sm text-foreground/60">{service.details}</p>
                      <Button
                        variant="outline"
                        className="w-full border-primary text-primary hover:bg-primary/5"
                        onClick={() => handleLearnMore(service.id)}
                      >
                        Learn More »
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center pt-8">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
                onClick={() => {
                  const contactSection = document.getElementById('contact-section');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                See All Our Services »
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
